import { CodenameTag, Modal, ModalProps } from "@vesatogo/grass-core"
import { inr, percent } from "@vesatogo/utils"
import { Fragment } from "react"
import PavtiNote from "~/components/PavtiNote"
import SummaryTable from "~/components/SummaryTable"
import { TRADE_KINDS } from "~/constants/static.items"
import { ReceiptState } from "~/pages/d/trade-requests/receipts/types"
import { getCommodityParticularsTotals } from "~/utils/receiptParticularsItems"

type ReceiptSummaryProps = ModalProps & {
  receipt?: ReceiptState
  isDisabled?: boolean
  isLoading?: boolean
}
const CreateReceiptSummary = ({
  isOpen,
  onClose,
  receipt,
  isLoading,
  isDisabled,
  ...props
}: ReceiptSummaryProps) => {
  return (
    <Modal
      bodyClassName="md:w-[50%] "
      contentClassName="!bg-gray-100 max-h-[70vh] md:max-h-none overflow-y-auto"
      title={TRADE_KINDS.find(kind => kind.id === receipt?.kind)?.name}
      isOpen={isOpen}
      onClose={onClose}
      {...props}
    >
      <div className="bg-white rounded">
        {receipt?.seller?.id && (
          <div className="bg-green-100 px-3 py-2 rounded-t">
            <div>
              <div className="font-500">
                {receipt?.seller?.name}{" "}
                <span className="text-gray-600 font-400">(supplier name)</span>
              </div>
              <CodenameTag
                referenceId={receipt?.seller?.external_reference_id}
              />
            </div>
          </div>
        )}
        <div className="flex flex-col gap-3 p-3 overflow-x-auto">
          {(() => {
            const r = receipt
            const unifiedTrades = (receipt?.trades || []).filter(
              trade => trade?.commodity_items?.length
            )
            const legacyTrades = (receipt?.trades || []).filter(
              trade => !trade?.commodity_items?.length
            )
            const unitName = (unit?: any) => unit?.name || r?.unit?.name || "kg"

            return (
              <>
                {unifiedTrades.length ? (
                  <table className="w-full border border-gray-200 rounded-md overflow-hidden">
                    <thead>
                      <tr className="bg-gray-200 text-left text-xs text-gray-600">
                        <th className="px-3 py-2">Buyer</th>
                        <th className="px-3 py-2">Commodity</th>
                        <th className="px-3 py-2">Grade</th>
                        <th className="px-3 py-2">Rate</th>
                        <th className="px-3 py-2">Qty</th>
                        <th className="px-3 py-2">Amt</th>
                      </tr>
                    </thead>
                    <tbody>
                      {unifiedTrades.map(trade => {
                        const blockGroups = (trade.commodity_items || []).map(
                          (block, cIdx) => {
                            const grades = block.grades?.length
                              ? block.grades
                              : [{ grade: null, rate: 0, quantity: 0 }]
                            const totals = getCommodityParticularsTotals(block)
                            const gstPct =
                              (block.supplier_igst_percentage || 0) +
                              (block.supplier_sgst_percentage || 0) +
                              (block.supplier_cgst_percentage || 0)
                            const gst =
                              gstPct > 0
                                ? totals.total_cost * (gstPct / 100)
                                : (block.supplier_igst || 0) +
                                  (block.supplier_sgst || 0) +
                                  (block.supplier_cgst || 0)
                            return {
                              block,
                              cIdx,
                              grades,
                              totals,
                              gst,
                              unit: block.quantity_unit || r?.unit,
                            }
                          }
                        )

                        const buyerMaterial = blockGroups.reduce(
                          (sum, g) => sum + g.totals.total_cost,
                          0
                        )
                        const buyerGst = blockGroups.reduce(
                          (sum, g) => sum + g.gst,
                          0
                        )
                        const buyerQty =
                          trade.total_quantity ??
                          blockGroups.reduce(
                            (sum, g) => sum + g.totals.quantity,
                            0
                          )
                        const buyerSpan =
                          blockGroups.reduce(
                            (sum, g) => sum + Math.max(g.grades.length, 1) + 1,
                            0
                          ) + 1

                        const itemizedAmt = (material: number, gst: number) => (
                          <div className="flex flex-col">
                            <span className="font-600">
                              {inr(material + gst)}
                            </span>
                          </div>
                        )

                        return (
                          <Fragment key={trade.buyer?.id}>
                            {blockGroups.flatMap((g, gIdx) => {
                              const firstRowOfBuyer = gIdx === 0
                              const rows = g.grades.map((gr, rowIdx) => {
                                const isFirstGradeRow = rowIdx === 0
                                return (
                                  <tr
                                    key={`${trade.buyer?.id}-${g.cIdx}-${rowIdx}`}
                                    className="border-b border-gray-100 !text-sm"
                                  >
                                    {firstRowOfBuyer && rowIdx === 0 && (
                                      <td
                                        rowSpan={buyerSpan}
                                        className="px-3 py-2 align-top font-500"
                                      >
                                        <div className="flex flex-wrap gap-2 items-center">
                                          {trade.buyer?.name}
                                          <CodenameTag
                                            referenceId={
                                              trade.buyer?.external_reference_id
                                            }
                                          />
                                        </div>
                                      </td>
                                    )}
                                    {isFirstGradeRow && (
                                      <td
                                        rowSpan={Math.max(g.grades.length, 1)}
                                        className="px-3 py-2 align-top text-gray-800"
                                      >
                                        {g.block.commodity?.name ||
                                          `Commodity ${g.cIdx + 1}`}
                                        <div className="text-gray-600">
                                          {g.block.commodity_variety?.name ||
                                            `Commodity_variety ${g.cIdx + 1}`}
                                        </div>
                                      </td>
                                    )}
                                    <td className="px-3 py-2">
                                      {gr.grade?.name || "—"}
                                    </td>
                                    <td className="px-3 py-2">
                                      {inr(gr.rate || 0)} / {unitName(g.unit)}
                                    </td>
                                    <td className="px-3 py-2">
                                      {gr.quantity} {unitName(g.unit)}
                                    </td>
                                    <td className="px-3 py-2">
                                      {inr(
                                        (Number(gr.quantity) || 0) *
                                          (Number(gr.rate) || 0)
                                      )}
                                    </td>
                                  </tr>
                                )
                              })
                              const totalRow = (
                                <tr
                                  key={`${trade.buyer?.id}-${g.cIdx}-total`}
                                  className="bg-gray-50 border-b border-gray-100 !text-sm"
                                >
                                  <td
                                    className="px-3 py-2 font-600 "
                                    colSpan={3}
                                  >
                                    Sub Total{" "}
                                    <span className="font-400 text-gray-600 text-xs">
                                      (i.e. Trade total + Commodity GST)
                                    </span>
                                  </td>
                                  <td
                                    className="px-3 py-2 text-right"
                                    colSpan={2}
                                  >
                                    {itemizedAmt(g.totals.total_cost, g.gst)}
                                  </td>
                                </tr>
                              )
                              return [...rows, totalRow]
                            })}
                            <tr
                              key={`${trade.buyer?.id}-buyer-total`}
                              className="bg-yellow-50 border-b border-gray-100 "
                            >
                              <td className="px-3 py-2 font-600" colSpan={3}>
                                Buyer Total
                              </td>

                              <td className="px-3 py-2 text-right" colSpan={2}>
                                {itemizedAmt(buyerMaterial, buyerGst)}
                              </td>
                            </tr>
                          </Fragment>
                        )
                      })}
                    </tbody>
                  </table>
                ) : null}

                {legacyTrades.map(trade => (
                  <SummaryTable
                    key={trade.buyer?.id}
                    header={
                      <tr className="!bg-yellow-100">
                        <th
                          colSpan={4}
                          className="font-500 px-3 gap-1 w-full py-[8px]"
                        >
                          <span className="flex gap-3">
                            {trade.buyer?.name}{" "}
                            <CodenameTag
                              referenceId={trade.buyer?.external_reference_id}
                            />
                          </span>
                        </th>
                      </tr>
                    }
                    data={trade?.items}
                    columns={[
                      {
                        name: "Grade",
                        accessor: "grade",
                        cell(value: any) {
                          return value?.name
                        },
                        footer() {
                          return "Total"
                        },
                      },
                      {
                        name: "Rate",
                        accessor: "rate",
                        cell(value: any) {
                          return (
                            <>
                              {inr(value)} / {r?.unit?.name}
                            </>
                          )
                        },
                      },
                      {
                        name: "Quantity",
                        accessor: "quantity",
                        cell(value: any) {
                          return (
                            <>
                              {value} {r?.unit?.name}
                            </>
                          )
                        },
                        footer() {
                          return (
                            <span className="font-500">
                              {trade.total_quantity} {r?.unit?.name}
                            </span>
                          )
                        },
                      },
                      {
                        name: "Amount",
                        accessor: "total_amount",
                        cell(value: any) {
                          return inr(value || 0)
                        },
                        footer() {
                          return (
                            <span className="font-500">
                              {inr(trade.total_amount || 0)}
                            </span>
                          )
                        },
                      },
                    ]}
                  />
                ))}
              </>
            )
          })()}

          <hr className="border-dashed" />
          <SummaryTable
            data={[
              {
                id: 1,
                type: "std",
              },
              {
                id: 2,
                type: "expense",
                name: "Total Expense",
              },
              {
                id: 3,
                type: "expense",
                name: "Supplier Expense",
              },
            ]}
            className={"text-sm"}
            columns={[
              {
                name: "Total",
                cell(_, { type, name, id }) {
                  return (
                    <span className={id !== 2 ? "text-red-500" : undefined}>
                      {type === "std"
                        ? `Standard Deduction - ${percent(
                            receipt?.standard_deduction || 0
                          )}`
                        : name}
                    </span>
                  )
                },
                footer() {
                  return (
                    <span className="text-lg text-green-400 font-600">
                      Supplier Payable
                    </span>
                  )
                },
              },
              {
                name: `${receipt?.total_quantity} Units`,
                cell(_, { type }) {
                  if (type === "expense") return null
                  return (
                    <span className="text-red-500">
                      {receipt?.quantity_deducted} {receipt?.unit?.name}
                    </span>
                  )
                },
              },
              {
                name: `${inr(receipt?.total_amount || 0)}`,
                cell(_, { type, id }) {
                  return (
                    <span className={id !== 2 ? "text-red-500" : undefined}>
                      {inr(
                        type === "std"
                          ? receipt?.amount_deducted || 0
                          : id === 2
                          ? receipt?.expense_amount || 0
                          : receipt?.seller_expense_amount || 0
                      )}
                    </span>
                  )
                },
                footer() {
                  return (
                    <span className="text-lg text-green-400 font-600">
                      {inr(receipt?.seller_receivable_amount || 0)}
                    </span>
                  )
                },
              },
            ]}
          />
        </div>
        <div className="p-3">
          {receipt?.kind && <PavtiNote kind={receipt?.kind} />}
        </div>
      </div>
    </Modal>
  )
}

export default CreateReceiptSummary
